/**
 * 🎚️ LikertScalePremium — Échelle 1-7 executive premium (Iter389)
 *
 * Refonte complète mobile-first → desktop avec :
 *   • Segments numérotés larges (boutons rounded-2xl 48-72px hauteur),
 *     pas de dots/cercles vides.
 *   • Gradient charbon → or sur le track + remplissage progressif.
 *   • Sélection : ring or premium + scale-up subtil + checkmark animé.
 *   • Ancres simplifiées : 2 ancres latérales SOUS la scale (pas de
 *     duplication verticale qui wrap sur 3 lignes).
 *   • ARIA radiogroup natif, navigation clavier ← → ↑ ↓ Home End 1-7.
 *   • Hover/focus halo doré + tap feedback (scale 0.97).
 *
 * API identique : { value, onSelect, disabled, questionId }.
 */
import React, { useRef, useCallback } from 'react';
import { Check } from 'lucide-react';

const T = {
  ivory: '#FBF9F3',
  ivoryDeep: '#FFFCEC',
  charcoal: '#2A2319',
  ink: '#1C1147',
  text: '#1F2937',
  muted: '#6B7280',
  border: '#E5DFD0',
  borderSoft: '#EBE4D2',
  gold: '#D4AF37',
  goldDeep: '#92710A',
  goldLight: '#E8D69A',
  goldGlow: 'rgba(212,175,55,0.35)',
};

// Couleur d'accent du segment (gradient charbon → or doux → or profond)
const SEG_COLORS = {
  1: '#3D352A',
  2: '#574A38',
  3: '#7A6840',
  4: '#A88A45',
  5: '#C7A547',
  6: '#D4AF37',
  7: '#92710A',
};

export default function LikertScalePremium({ value, onSelect, disabled, questionId }) {
  const ref = useRef(null);

  const handleKeyDown = useCallback((e, v) => {
    if (disabled) return;
    let next = null;
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') next = Math.min(7, v + 1);
    else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') next = Math.max(1, v - 1);
    else if (e.key === 'Home') next = 1;
    else if (e.key === 'End') next = 7;
    else if (/^[1-7]$/.test(e.key)) next = parseInt(e.key, 10);
    if (next != null) {
      e.preventDefault();
      const btn = ref.current?.querySelector(`[data-likert-value="${next}"]`);
      btn?.focus();
    } else if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onSelect(v);
    }
  }, [disabled, onSelect]);

  return (
    <div
      ref={ref}
      role="radiogroup"
      aria-label="Échelle d'accord — 1 (pas du tout) à 7 (totalement)"
      data-testid="likert-premium-root"
      className="select-none"
    >
      {/* Track segmenté premium */}
      <div className="relative">
        {/* Ligne de fond dégradée (sous les segments) */}
        <div
          aria-hidden
          className="absolute left-2 right-2 top-1/2 -translate-y-1/2 h-[2px] rounded-full pointer-events-none"
          style={{
            background: `linear-gradient(90deg, ${SEG_COLORS[1]} 0%, ${SEG_COLORS[4]} 50%, ${SEG_COLORS[7]} 100%)`,
            opacity: 0.18,
          }}
        />

        {/* 7 segments numérotés */}
        <div className="grid grid-cols-7 gap-1.5 sm:gap-2.5 relative">
          {[1, 2, 3, 4, 5, 6, 7].map((v) => {
            const selected = value === v;
            const accent = SEG_COLORS[v];
            return (
              <button
                key={v}
                type="button"
                role="radio"
                aria-checked={selected}
                aria-label={`${v} sur 7`}
                tabIndex={selected || (!value && v === 4) ? 0 : -1}
                data-likert-value={v}
                data-testid={`discovery-likert-${questionId}-${v}`}
                disabled={disabled}
                onClick={() => onSelect(v)}
                onKeyDown={(e) => handleKeyDown(e, v)}
                className="likert-seg group relative flex items-center justify-center rounded-2xl transition-all duration-200 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
                style={{
                  height: 'clamp(56px, 11vw, 76px)',
                  background: selected
                    ? `linear-gradient(160deg, ${accent} 0%, ${SEG_COLORS[Math.min(7, v + 1)]} 100%)`
                    : '#FFFFFF',
                  border: selected
                    ? `1.5px solid ${accent}`
                    : `1px solid ${T.borderSoft}`,
                  boxShadow: selected
                    ? `0 14px 32px -14px ${accent}99, 0 0 0 4px ${T.ivoryDeep}, 0 0 0 5px ${accent}55`
                    : '0 1px 0 rgba(255,255,255,0.6) inset, 0 4px 12px -8px rgba(28,17,71,0.08)',
                  transform: selected ? 'translateY(-2px)' : 'translateY(0)',
                  cursor: disabled ? 'not-allowed' : 'pointer',
                }}
              >
                {/* Numéro */}
                <span
                  className="font-serif tabular-nums leading-none transition-all"
                  style={{
                    fontFamily: '"Cormorant Garamond", "Playfair Display", Georgia, serif',
                    fontSize: 'clamp(20px, 4.2vw, 28px)',
                    color: selected ? '#FFFFFF' : accent,
                    fontWeight: selected ? 700 : 600,
                    textShadow: selected ? '0 1px 2px rgba(0,0,0,0.15)' : 'none',
                  }}
                >
                  {v}
                </span>

                {/* Checkmark mini (apparait sur sélection) */}
                {selected && (
                  <span
                    aria-hidden
                    className="absolute -top-2 -right-2 flex items-center justify-center rounded-full"
                    style={{
                      width: 22,
                      height: 22,
                      background: '#FFFFFF',
                      boxShadow: `0 4px 10px -2px ${accent}66`,
                      border: `1.5px solid ${accent}`,
                    }}
                  >
                    <Check className="w-3 h-3" style={{ color: accent, strokeWidth: 3 }} />
                  </span>
                )}

                {/* Halo doré au hover (non sélectionné) */}
                {!selected && (
                  <span
                    aria-hidden
                    className="pointer-events-none absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100 transition-opacity duration-200"
                    style={{
                      background: `radial-gradient(circle at 50% 50%, ${accent}14 0%, transparent 70%)`,
                      boxShadow: `inset 0 0 0 1.5px ${accent}55`,
                    }}
                  />
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Ancres latérales (gauche / droite uniquement, plus de duplication) */}
      <div
        className="mt-5 flex items-start justify-between gap-3 px-0.5"
      >
        <div className="flex flex-col items-start text-left max-w-[42%]">
          <span
            className="text-[10px] sm:text-[11px] uppercase tracking-[0.22em] font-semibold"
            style={{ color: T.goldDeep }}
          >
            ← Pas du tout
          </span>
          <span
            className="text-[11px] sm:text-[12px] mt-1 leading-snug"
            style={{ color: T.muted, fontStyle: 'italic' }}
          >
            d'accord avec cette affirmation
          </span>
        </div>
        <div className="flex flex-col items-end text-right max-w-[42%]">
          <span
            className="text-[10px] sm:text-[11px] uppercase tracking-[0.22em] font-semibold"
            style={{ color: T.goldDeep }}
          >
            Totalement →
          </span>
          <span
            className="text-[11px] sm:text-[12px] mt-1 leading-snug"
            style={{ color: T.muted, fontStyle: 'italic' }}
          >
            d'accord avec cette affirmation
          </span>
        </div>
      </div>

      {/* Hint clavier desktop (caché mobile) */}
      <div
        className="hidden sm:flex items-center justify-center mt-4 gap-2 text-[10.5px] uppercase tracking-[0.18em]"
        style={{ color: T.muted, opacity: 0.6 }}
      >
        <kbd className="px-1.5 py-0.5 rounded border" style={{ borderColor: T.borderSoft, fontFamily: 'monospace' }}>1-7</kbd>
        <span>· ou flèches</span>
        <kbd className="px-1.5 py-0.5 rounded border" style={{ borderColor: T.borderSoft, fontFamily: 'monospace' }}>←</kbd>
        <kbd className="px-1.5 py-0.5 rounded border" style={{ borderColor: T.borderSoft, fontFamily: 'monospace' }}>→</kbd>
      </div>

      {/* Style local : active feedback (scale légère au tap) */}
      <style>{`
        .likert-seg:active:not(:disabled) {
          transform: translateY(-1px) scale(0.97);
        }
      `}</style>
    </div>
  );
}
